import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast, Toaster } from "sonner";
import InstallPWAButton from "@/components/InstallPWAButton";
import { landingPathFor } from "@/lib/billing";

const REASON_MESSAGES = {
  idle_timeout: "Sesi Anda telah berakhir karena tidak ada aktivitas. Silakan login kembali.",
  absolute_timeout: "Sesi maksimum Anda telah tercapai. Silakan login kembali.",
  revoked: "Sesi Anda telah dicabut. Silakan login kembali.",
};

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionMessage, setSessionMessage] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const reason = searchParams.get("reason");
    if (reason && REASON_MESSAGES[reason]) {
      setSessionMessage(REASON_MESSAGES[reason]);
      // Clean the query string so the message doesn't re-appear on refresh.
      const next = new URLSearchParams(searchParams);
      next.delete("reason");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(username, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Berhasil masuk");
      navigate(landingPathFor(r.user));
    } else {
      setError(r.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8F9FA]">
      {/* LEFT: brand image */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1691435828932-911a7801adfb"
          alt="Fiber optic network"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/60" />
        <div className="absolute inset-0 bg-grid-dark opacity-60" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-5">
            <img
              src="/logo-transparan.png"
              alt="AROMA NET"
              data-testid="login-logo"
              className="h-24 w-auto object-contain"
              style={{ filter: "invert(1) hue-rotate(180deg)" }}
            />
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/70">
              Jadwal Operasional
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/60 mb-3">
              Command Center — PT AROMA NET MEDIA
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">
              Operasional Harian.
              <br />
              Terpusat &amp; Terkendali.
            </h1>
            <p className="mt-4 text-white/70 max-w-md leading-relaxed">
              Kelola pemasangan, perbaikan, teknisi, absensi, billing, dan aktivitas operasional
              — semuanya dalam satu tempat.
            </p>
          </div>

          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/40">
            v1.5 · Internal Use Only
          </div>
        </div>
      </div>

      {/* RIGHT: form */}
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-4 mb-8">
            <img
              src="/logo-transparan.png"
              alt="AROMA NET"
              data-testid="login-logo-mobile"
              className="h-16 w-auto object-contain"
            />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#4B5563]">Jadwal Operasional</span>
          </div>

          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF] mb-2">
            Selamat Datang
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            Masuk ke Akun
          </h2>
          <p className="text-sm text-[#4B5563] mt-2">
            Gunakan username &amp; password yang telah diberikan admin.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-[#4B5563] mb-2">
                Username
              </label>
              <input
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                className="w-full h-11 px-4 rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent transition"
                placeholder="admin"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-[#4B5563] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="w-full h-11 px-4 pr-11 rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent transition"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#0A0A0A]"
                  aria-label="Toggle password"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {sessionMessage && (
              <div
                data-testid="login-session-message"
                className="text-sm text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-3 py-2"
              >
                {sessionMessage}
              </div>
            )}

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Masuk
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-[#E5E7EB] text-xs text-[#9CA3AF]">
            Butuh akses? Hubungi Administrator sistem AROMA NET.
          </div>
        </form>
        <Toaster position="top-right" richColors />
      </div>
      <InstallPWAButton />
    </div>
  );
}
